import { Chip } from '@mui/material';
import {
  GridFilterInputSingleSelect,
  GridFilterOperator,
  GridSingleSelectColDef,
} from '@mui/x-data-grid-pro';

import { FilterParam } from 'common/src/query-params/FilterParam';
import { SingleOperators } from 'common/src/query-params/operators';

import { singleSelectFilter } from '../Filters/CustomCollectors';

type ColorOptions = 'success' | 'info' | 'error' | undefined;

type TriStateOptions<Row, L extends string> = {
  /**
   * The function to get the label for a given row. A string must be returned.
   */
  getLabel: (row: Row) => L;
  /**
   * The three possible values in order.
   * Index 0 renders as plain text, 1 in grey, 2 in green.
   */
  valueOptions: [L, L, L];
  /** Optional width of the column. */
  width?: number;
  /** Optional colors for the three states. */
  colors?: [ColorOptions, ColorOptions, ColorOptions];
  /** Specifies whether filtering is handled client-side or server-side. */
  filterMode?: 'client' | 'server';
  /** Optional additional server-side filters for a selected value. */
  serverAdditionalFilters?: (value: L) => FilterParam[];
  /** Whether to replace the column's value filter when providing server filters. */
  replaceBaseFilter?: (value: L) => boolean;
  /** Whether to disable the filter input component. */
  disableFilterInput?: boolean;
};

/**
 * A helper to generate some params for a tri-state MUI column along with
 * custom filter and sort operators.
 */
export default function addTriStateColumnProps<Row, L extends string>({
  getLabel,
  valueOptions,
  width = 130,
  colors = [undefined, 'info', 'success'],
  filterMode = 'client',
  serverAdditionalFilters,
  replaceBaseFilter,
  disableFilterInput = false,
}: TriStateOptions<Row, L>): Partial<GridSingleSelectColDef> {
  const filterOperators: GridFilterOperator[] =
    filterMode === 'server'
      ? singleSelectFilter(
          {
            service: {
              data: valueOptions.map((value, index) => ({
                id: index + 1,
                label: value,
                value,
                additionalFilters: serverAdditionalFilters?.(value),
                replaceBaseFilter: replaceBaseFilter?.(value),
              })),
              fieldset: { key: 'id', label: 'label', value: 'value' },
            },
            disabled: disableFilterInput,
          },
          [
            SingleOperators.GREATER_THAN,
            SingleOperators.GREATER_THAN_OR_EQUAL_TO,
            SingleOperators.LESS_THAN,
            SingleOperators.LESS_THAN_OR_EQUAL_TO,
            SingleOperators.NOT_EQUAL_TO,
          ],
        )
      : [
          {
            label: 'Is',
            value: 'is',
            requiresFilterValue: true,
            getApplyFilterFn: (item) => {
              const target = item.value as string;

              if (!target) {
                return null;
              }

              return (value: unknown) => String(value ?? '') === target;
            },
            InputComponent: GridFilterInputSingleSelect,
          },
        ];

  const sortComparator = (a, b) => {
    // Map labels to a rank used for sorting (based on provided order)
    const rank = new Map<string, number>([
      [String(valueOptions[0]), 0],
      [String(valueOptions[1]), 1],
      [String(valueOptions[2]), 2],
    ]);
    const ra = rank.get(a);
    const rb = rank.get(b);

    if (ra !== rb) return ra - rb;

    return a.localeCompare(String(b ?? ''));
  };

  return {
    type: 'singleSelect',
    width,
    display: 'flex', // Needed to allow align prop below to work.
    align: 'center',
    headerAlign: 'center',
    filterOperators,
    sortComparator,
    valueOptions,
    valueGetter: (_value, row) => getLabel(row as Row),
    renderCell: ({ row }) => {
      let color: 'success' | 'info' | 'error' | undefined;
      const label = getLabel(row as Row) as L;

      if (label === valueOptions[2]) {
        color = colors[2];
      } else if (label === valueOptions[1]) {
        color = colors[1];
      }

      return label === valueOptions[0] && !colors[0] ? (
        label
      ) : (
        <Chip variant="outlined" size="medium" label={label} color={color} />
      );
    },
  };
}
